import { mapActions, mapGetters, mapState } from 'vuex';
import alertBox from '@/components/generics/alert-box/alert-box.vue';
import plusMinusSign from '@/components/generics/plus-minus-sign/plus-minus-sign.vue';
import { roundToTwo, toCents, hasNoPositionInAMarketBesidesLoadedContract } from '@/utils/helpers'
import { MODALS } from '@/components/app/app-modals/app-modals';
import Contract from '@/models/contract';
import notificationProxy from '@/services/notifications/proxy';
import purchaseExtendedBetslipContractInfo
  from '@/components/purchase/sushi/purchase-extended-betslip-contract-info/purchase-extended-betslip-contract-info.vue';
import loaderMixin from '@/mixins/loader';

export default {
  mixins: [
    loaderMixin
  ],
  props: {
    market: {},
  },
  components: {
    alertBox,
    plusMinusSign,
    purchaseExtendedBetslipContractInfo,
  },
  data() {
    return {
      MODALS,
      isExpanded: false,
      isEnabled: false,
      timeout: null,
    };
  },
  computed: {
    ...mapGetters('app', ['isLayoutDesktop', 'isLayoutMobile']),
    ...mapState('trade', ['tradeOptions', 'confirmationData']),
    dontShowMultiples() {
      return !this.isYesType && hasNoPositionInAMarketBesidesLoadedContract({ market: this.market, contract: this.contract });
    },
    contract() {
      if (!this.tradeOptions.contractId) return null;
      const contracts = this.market.contracts || this.market.marketContracts;
      return contracts.find(c => c.contractId === this.tradeOptions.contractId);
    },
    contractModel() {
      return new Contract(this.contract);
    },
    outsideOfPriceBoundsWarningText() {
      let value = null;
      const threshold = 10;
      if (this.isBuyTradeType) {
        value = this.isYesType ? this.contractModel.buyYesCostAsCents : this.contractModel.buyNoCostAsCents;
      } else {
        value = this.isYesType ? this.contractModel.sellYesCostAsCents : this.contractModel.sellNoCostAsCents;
      }

      if (!value) {
        return '';
      }

      if (this.isBuyTradeType && this.pricePerShare > (value + threshold)) {
        return 'This order is more than 10¢ above the current market price. Please verify order before submitting.';
      }

      if (!this.isBuyTradeType && this.pricePerShare < (value - threshold)) {
        return 'This order is more than 10¢ below the current market price. Please verify order before submitting.';
      }
      return '';
    },
    pricePerShare() {
      return toCents(this.confirmationData.pricePerShare);
    },
    pricePerShareDisplay() {
      return `${this.pricePerShare}¢`;
    },
    pricePerSharePrefix() {
      return this.confirmationData.tradeType <= 1 ? 'Max' : 'Min';
    },
    tradeTypeText() {
      return this.isBuyTradeType ? 'Buy' : 'Sell';
    },
    isBuyTradeType() {
      return this.confirmationData.tradeType <= 1;
    },
    isYesType() {
      return this.confirmationData.tradeType === 1 || this.confirmationData.tradeType === 3;
    },
    isNoType() {
      return this.confirmationData.tradeType === 0 || this.confirmationData.tradeType === 2;
    },
    yesNoText() {
      return this.isYesType ? 'YES' : 'NO';
    },
    classModifiers() {
      return {
        'purchase-review-and-confirm-sushi': true,
        'purchase-review-and-confirm-sushi--yes': this.isYesType,
        'purchase-review-and-confirm-sushi--no': this.isNoType,
      };
    },
    profits() {
      return this.confirmationData.tradeProfit || 0;
    },
    fees() {
      return this.confirmationData.fee || null;
    },
    riskAdjustment() {
      return this.confirmationData.estimatedRisk || null;
    },
    orderTotal() {
      return this.confirmationData.orderTotal;
    },
    showProfitOrLoss() {
      const types = [2, 3];
      const tradeType = this.confirmationData.tradeType;
      return (this.profits !== 0) || (types.includes(tradeType));
    },
    profitOrLoss() {
      const profit = this.profits || 0;
      return (profit > 0) ? 'Profit' : 'Loss';
    },
    payoutDisplay() {
      if (this.tradeOptions.operationType === 'sell') {
        const price = this.tradeOptions.quantity * this.tradeOptions.pricePerShare;
        if (price < 100) {
          return `${price}¢+`;
        }
        return `$${roundToTwo(this.tradeOptions.quantity * this.tradeOptions.pricePerShare / 100).toFixed(2)}+`;
      }
      return `$${this.tradeOptions.quantity}`;
    },
    tradeType() {
      switch (`${this.tradeOptions.operationType}_${this.tradeOptions.operationValue}`) {
        case 'buy_no': return 0;
        case 'buy_yes': return 1;
        case 'sell_no': return 2;
        case 'sell_yes': return 3;
        default: return -1;
      }
    },
    nextButtonDisabled() {
      return !this.confirmationData.showSubmitTradeButton || !this.isEnabled;
    },
    nextButtonClassModifiers() {
      return {
        'purchase-review-and-confirm-sushi__footer-next-button': true,
        'purchase-review-and-confirm-sushi__footer-next-button--yes': this.isYesType && !this.nextButtonDisabled,
        'purchase-review-and-confirm-sushi__footer-next-button--no': !this.isYesType && !this.nextButtonDisabled,
        'purchase-review-and-confirm-sushi__footer-next-button--disabled': this.nextButtonDisabled,
      };
    },
    buyPrice() {
      return roundToTwo(this.tradeOptions.quantity * this.pricePerShare / 100).toFixed(2);
    },
    multiple() {
      const result = 100 / this.pricePerShare
      const rounded = Math.round(result * 100) / 100
      return rounded % 1 === 0 ? Math.round(rounded) : rounded;
    },
  },
  methods: {
    ...mapActions('popup', ['setCurrentSmallPopup', 'setGenericSmallPopup', 'setCurrentPopupOptions', 'setCurrentPopup', 'setTradePopupOptions']),
    ...mapActions('profile', ['clearTradedMarkets']),
    ...mapActions('trade', [
      'setTradeOptions', 'clearOrderBook',
      'setConfirmationData', 'setConfirmationDoneData',
      'setIsInConfirmationMode', 'submitTrade', 'setTradeSuccess',
      'setBottomBetSlipExtendedMode'
    ]),
    ...mapActions('notifications', ['addSimple']),
    ...mapActions('market', ['setMarketEngineBusyMessage']),
    onReadRules() {
      this.setGenericSmallPopup({
        name: MODALS.SMALL_MARKET_RULES,
        options: {
          title: 'The Rules',
          market: this.market,
        },
      });
    },
    cancel() {
      this.$emit('cancel');
    },
    getErrorFromResponse(response) {
      if (!response) return '';
      if (typeof response.data === 'string') {
        return response.data;
      }
      return (response.data) ? response.data.message : '';
    },
    onClose() {
      this.setCurrentPopup();
      this.setTradePopupOptions({});
      // this.setTradeOptions(null);
    },
    onClickAmend() {
      this.setConfirmationData(null);
      this.setConfirmationDoneData(null);
      this.setIsInConfirmationMode(false);
      this.onClose();
    },
    onClickSubmit() {
      this.loaderAdd();
      const payload = {
        quantity: this.tradeOptions.quantity,
        pricePerShare: this.tradeOptions.pricePerShare,
        contractId: this.tradeOptions.contractId,
        tradeType: this.tradeType,
      }

      this.submitTrade(payload)
        .then((response) => {
          const data = response.data;
          this.$gtm.trackEvent({ event: 'Trade' });
          this.loaderRemove();
          this.setConfirmationData(null);
          this.setConfirmationDoneData(data);
          this.addSimple({
            externalId: (data.offer) ? (data.offer.offerId) : null,
            closeTimeout: 3500,
            icon: 'pending',
            text: 'You will be notified when processing is complete.',
            title: 'Order Received',
          });
          this.setMarketEngineBusyMessage({
            isEngineBusy: data.isEngineBusy,
            isEngineBusyMessage: data.isEngineBusyMessage,
          });
          this.setTradeSuccess(true);
          this.setBottomBetSlipExtendedMode(false);
          this.clearTradedMarkets();
          const isBuyTradeType = [0, 1].includes(this.tradeType);
          notificationProxy.showMessagingJourney(isBuyTradeType);
        })
        .catch((error) => {
          this.setTradeSuccess(false);
          this.loaderRemove();
          const message = this.getErrorFromResponse(error.response);
          if (message) {
            this.addSimple({
              title: 'Error Occurred',
              icon: 'cancel',
              text: message,
            });
          }
        });
    },
  },
  beforeDestroy() {
    this.setIsInConfirmationMode(false);
    if (this.timeout !== null) {
      clearTimeout(this.timeout);
    }
  },
  mounted() {
    this.timeout = setTimeout(() => {
      this.isEnabled = true;
    }, 0);
  },
};



// WEBPACK FOOTER //
// ./src/components/purchase/sushi/purchase-review-and-confirm-sushi/purchase-review-and-confirm-sushi.js